// SPDX-License-Identifier: MIT
// Azure SAS session state (browser-only, in-memory; cleared on tab close)
//
// What this is:
// - A minimal, ephemeral store for the active SAS URL and a few derived fields
// - Not persisted (no localStorage, IndexedDB). Credentials vanish on refresh
// - Exposes helpers for auth UI to check "connected" state, expiry, and permissions
//
// Why in-memory only:
// - Security-first: we avoid writing sensitive tokens to disk or long-lived storage
// - Matches Zero Trust posture in security docs; best-effort cleanup on unload

let sasUrlInternal: string | null = null; // full SAS URL (container scope)
let expiryEpoch: number | null = null;
let permissions: string | null = null;

export interface AzureAuthSessionInfo {
  connected: boolean;
  expiresAt?: number; // ms epoch
  permissions?: string;
  minutesRemaining?: number;
}

/**
 * Set the active SAS session using a full SAS URL (container or directory scope).
 * Also parses and caches expiry (se) and permissions (sp) when present.
 */
export function setSasUrl(url: string) {
  sasUrlInternal = url;
  try {
    const u = new URL(url);
    const params = u.searchParams;
    const se = params.get('se');
    const sp = params.get('sp');
    permissions = sp || null;
    if (se) {
      const exp = Date.parse(se);
      if (!Number.isNaN(exp)) {
        expiryEpoch = exp;
      }
    }
  } catch {
    // Ignore parse errors (should not happen for valid SAS URL)
  }
}

/**
 * Remove all in-memory session data. Useful for logout and on tab close.
 */
export function clearSession() {
  sasUrlInternal = null;
  expiryEpoch = null;
  permissions = null;
}

/**
 * Return the full SAS URL currently held in memory, or null if not connected.
 */
export function getSasUrl(): string | null {
  return sasUrlInternal;
}

/**
 * Summarize connection status and derived fields for UI.
 */
export function getSessionInfo(): AzureAuthSessionInfo {
  if (!sasUrlInternal) return { connected: false };
  const now = Date.now();
  const minutesRemaining = expiryEpoch
    ? Math.max(0, Math.floor((expiryEpoch - now) / 60000))
    : undefined;
  return {
    connected: true,
    expiresAt: expiryEpoch || undefined,
    permissions: permissions || undefined,
    minutesRemaining,
  };
}

/**
 * Convenience boolean for quickly checking if a SAS is present.
 */
export function isAzureAuthenticated(): boolean {
  return Boolean(sasUrlInternal);
}

// Cleanup on tab close (best-effort credential hygiene)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    clearSession();
  });
}
